import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    clearLTCPDetails,
    clearParticipantAuditEventSearchData,
    createLTCPDetails,
    loadLTCPDetails,
    loadParticipantAuditEvent,
    updateLTCPDetails
} from '../../../store/participantService';
import { EDIT_ON_CLOSED_ROLES } from '../../../utils/formValidation/commonConstants';
import { NO_CURRENT_PARTICIPANT_ERROR } from '../../../utils/formValidation/commonErrorConstants';
import { ADVISER, hasRole, MANAGER, QUALITY, SUPERUSER } from '../../../utils/userRoles';
import AuditLTCPModal from '../../auditing/auditLTCP/AuditLTCPModal';
import Button from '../../formElements/Button';
import DateSelect from '../../formElements/DateSelect';
import Switch from '../../formElements/Switch';
import LoadingSpinner from '../../ui/LoadingSpinner';

import { validate } from './validateLTCP';

import form from '../../../commonStyles/formStyles.module.css';
import local from './ltcp.module.css';

const acceptedRoles = [ADVISER, MANAGER, QUALITY, SUPERUSER];
const _CREATE_MESSAGE = 'Participant LTCP details have been created';
const _UPDATE_MESSAGE = 'Participant LTCP details have been updated';

const LTCP = () => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        participantId: '',
        dateOfHypertensionDiagnosis: ''
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [initialStateOnEntry, setInitialStateOnEntry] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [isDisabled, setIsDisabled] = useState(true);
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);
    const [isAuditable, setIsAuditable] = useState(false);
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [auditLabel, setAuditLabel] = useState('');

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const ltcpDetails = useSelector((state) => state.entities.participantService.ltcpDetails);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USEEFFECTS
    useEffect(() => () => cleanUp(), []);

    useEffect(() => {
        if ('id' in currentParticipant) dispatch(loadLTCPDetails(currentParticipant.id));
    }, [currentParticipant?.id]);

    useEffect(() => {
        if ('status' in currentParticipant) {
            const disableOnClosedStatus =
                currentParticipant.status === 'Closed' && !hasRole(EDIT_ON_CLOSED_ROLES, roles);
            setIsDisabled(!hasRole(acceptedRoles, roles) || disableOnClosedStatus);
        }
    }, [currentParticipant?.status, roles]);

    useEffect(() => {
        if (ltcpDetails?.participantId !== currentParticipant?.id) return;
        if (Object.keys(ltcpDetails)?.length) {
            setNewEntry({ ...ltcpDetails });
            setInitialStateOnEntry({ ...ltcpDetails });
        }
    }, [ltcpDetails, currentParticipant?.id]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === _CREATE_MESSAGE || successMessage === _UPDATE_MESSAGE)
            setIsUpdateButtonDisabled(false);
    }, [successMessage]);

    // HELPER FNS
    const loadingError = () => {
        if (Object.keys(currentParticipant)?.length < 1) return NO_CURRENT_PARTICIPANT_ERROR;
    };

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false } }));
    };

    const cleanUp = () => dispatch(clearLTCPDetails());

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onSearchAuditItems = (field) => {
        if (field && newEntry?.id) {
            dispatch(loadParticipantAuditEvent('ltcp-data', newEntry.id, field));
            setShowAuditModal(true);
            setAuditLabel(field);
        }
    };

    const onAuditModalClose = (e, reason) => {
        e.stopPropagation();
        if (reason === 'backdropClick') return;
        setShowAuditModal(false);
        setAuditLabel('');
        dispatch(clearParticipantAuditEventSearchData());
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsUpdateButtonDisabled(true);
        const newErrors = validate(newEntry, initialStateOnEntry);
        if (Object.values(newErrors).some((el) => el.error)) {
            setErrors(newErrors);
            return;
        }
        newEntry?.id
            ? dispatch(updateLTCPDetails(newEntry, _UPDATE_MESSAGE))
            : dispatch(
                  createLTCPDetails(
                      { ...newEntry, id: null, participantId: currentParticipant.id },
                      _CREATE_MESSAGE
                  )
              );
    };

    // RENDER
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    return (
        <div>
            {newEntry?.id && (
                <div className={local.headingContent} data-testid="ltcp">
                    <Switch
                        id={'auditDisplayFields'}
                        label="Show Audit Fields"
                        onOff={true}
                        customClass="auditSwitch"
                        checked={isAuditable}
                        onChange={(e) => setIsAuditable(e.target.checked)}
                    />
                </div>
            )}

            <div className={form.formWrapper}>
                <form onSubmit={onSubmit} data-testid="form_start_ltcp" className={form.form}>
                    <div className={form.formSection}>
                        <div className={form.formColumn}>
                            <DateSelect
                                id="dateOfHypertensionDiagnosis"
                                label="Date of Hypertension Diagnosis"
                                disabled={isDisabled}
                                value={newEntry.dateOfHypertensionDiagnosis}
                                isAuditable={isAuditable}
                                onSearchAuditItems={onSearchAuditItems}
                                error={errors.dateOfHypertensionDiagnosis}
                                onDateChange={(date) =>
                                    onUpdate('dateOfHypertensionDiagnosis', date)
                                }
                            />
                        </div>
                    </div>

                    <Button
                        id="ltcpUpdateButton"
                        disabled={isDisabled || isUpdateButtonDisabled}
                        content="Update LTCP"
                        error={errors.button}
                        clearError={() => clearError('button')}
                    />
                </form>
            </div>
            {showAuditModal && (
                <div id="auditLTCPModal">
                    <AuditLTCPModal
                        isOpen={showAuditModal}
                        label={auditLabel}
                        onClose={onAuditModalClose}
                    />
                </div>
            )}
        </div>
    );
};

export default LTCP;
